/**
 * Three-band paint for a single-rating matrix row (C 4.3).
 *
 * Rows stay on the shared 3-band helpers (`divergingBands`,
 * `divergingRowFromDistribution`, `divergingLayout`). Do not invent a fourth
 * grouping. Favourable pole is `--slide-chart-1` (hsl(161 94% 30%)), matched
 * to NumericChart's single-bar green so a report page doesn't show two
 * different favorable greens across chart types. Never the deep brand /
 * `--chart-diverging-positive` green and never `--slide-diverging-*`.
 *
 * On a lower-is-better scale the low/high bands swap sides so the favourable
 * band stays on the positive (right) pole; on a middle-is-best scale the
 * middle band is the favourable one and both ends paint negative. Polarity is
 * declared (`polarity`) only — never inferred from anchor text.
 *
 * The positive colour claims
 * favorable, so a question that declares its rating unit is banded by the
 * canonical policy (`ratingPolicyBands`: 1-10 reads 1-3 | 4-7 | 8-10). Every
 * band is labelled with its range. A question with no declared unit keeps the
 * visual range split (`divergingBands`), its own named statistic
 * (`visual_range_band`, catalogue row `calc.rating.visual_range_band`): each
 * paint carries the statistic its bands measure, and the range band is labelled
 * by its declared range, never "favorable".
 */

import type { RatingPolarity } from '@digital-twins/shared-types'

import {
  divergingBands,
  divergingLayout,
  DivergingBands,
  DivergingBandStatistic,
  DivergingRow,
  ratingPolicyBands,
} from '../diverging'

export const THREE_BAND_NEGATIVE = 'hsl(var(--chart-negative))'
export const THREE_BAND_NEUTRAL = 'hsl(var(--chart-neutral))'
// `--slide-chart-1` is already a full `hsl(...)` (tokens.css / brand apply).
// Wrapping it again as `hsl(var(--slide-chart-1))` is invalid CSS — DevTools
// still shows a green swatch from the inner token, but the fill never paints.
export const THREE_BAND_POSITIVE = 'var(--slide-chart-1)'

export interface MatrixColumnDef {
  value: string
  numericValue: number
  text: string
}

export interface ThreeBandPaint {
  key: 'low' | 'neutral' | 'high'
  /** The statistic the painted bands measure (the range band is never favorable share). */
  statistic: DivergingBandStatistic
  leftPct: number
  widthPct: number
  color: string
  percent: number
  label: string
  edge: 'left' | 'right' | 'center'
}

const POLARITIES: ReadonlyArray<RatingPolarity> = ['higher_is_better', 'lower_is_better', 'middle_is_best']

function asPolarity(value: unknown): RatingPolarity | null {
  return POLARITIES.includes(value as RatingPolarity) ? (value as RatingPolarity) : null
}

function readExplicitPolarity(source: unknown): RatingPolarity | null {
  if (!source || typeof source !== 'object') {
    return null
  }
  const record = source as Record<string, unknown>
  const own = asPolarity(record.polarity)
  if (own !== null) {
    return own
  }
  const anchors = record.anchors
  return anchors && typeof anchors === 'object' ? asPolarity((anchors as Record<string, unknown>).polarity) : null
}

/**
 * Per-question polarity. Multi-measure arrays are unreachable from both
 * MatrixChart call sites (D7); do not silently paint from measure 0.
 */
function readPolarityFromMeasures(measures: unknown): RatingPolarity | null {
  if (!Array.isArray(measures) || measures.length === 0) {
    return null
  }
  if (measures.length > 1) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        'readPolarity: measures.length > 1 is not a polarity source; defaulting to higher_is_better',
      )
    }
    return 'higher_is_better'
  }
  return readExplicitPolarity(measures[0])
}

/**
 * The question's declared polarity, higher_is_better when none is declared.
 * Does not read label text.
 *
 * Lookup order: compiled `_canonical.polarity` (rating), then
 * `_canonical.measures[0]` (single-measure matrix: its anchors), then the
 * pre-compile question shapes (`polarity`, `anchors`, top-level `measures`).
 */
export function readPolarity(source: unknown): RatingPolarity {
  if (!source || typeof source !== 'object') {
    return 'higher_is_better'
  }
  const record = source as Record<string, unknown>
  const canonical = record._canonical
  if (canonical && typeof canonical === 'object') {
    const canonicalRecord = canonical as Record<string, unknown>
    const fromCanonical = readExplicitPolarity(canonicalRecord)
    if (fromCanonical !== null) {
      return fromCanonical
    }
    const fromCanonicalMeasures = readPolarityFromMeasures(canonicalRecord.measures)
    if (fromCanonicalMeasures !== null) {
      return fromCanonicalMeasures
    }
  }
  const own = readExplicitPolarity(source)
  if (own !== null) {
    return own
  }
  return readPolarityFromMeasures(record.measures) ?? 'higher_is_better'
}

export function distributionFromMatrixRow(
  row: Record<string, unknown>,
  columnDefs: Array<MatrixColumnDef>,
): Record<number, number> {
  const distribution: Record<number, number> = {}
  columnDefs.forEach((col) => {
    if (!Number.isFinite(col.numericValue)) {
      return
    }
    const count = Number(row[col.text] ?? row[col.value] ?? 0) || 0
    if (count > 0) {
      distribution[col.numericValue] = (distribution[col.numericValue] || 0) + count
    }
  })
  return distribution
}

export function distributionFromSegmentItems(
  items: Array<{ value?: string; count?: number }>,
): Record<number, number> {
  const distribution: Record<number, number> = {}
  items.forEach((item) => {
    const value = Number(item.value)
    const count = Number(item.count) || 0
    if (!Number.isFinite(value) || count <= 0) {
      return
    }
    distribution[value] = (distribution[value] || 0) + count
  })
  return distribution
}

export function pooledDistributionFromSegments(
  segments: Array<{ distribution?: Array<{ value?: string; count?: number }> }>,
): Record<number, number> {
  const pooled: Record<number, number> = {}
  segments.forEach((segment) => {
    const part = distributionFromSegmentItems(segment.distribution || [])
    Object.entries(part).forEach(([key, count]) => {
      const value = Number(key)
      pooled[value] = (pooled[value] || 0) + count
    })
  })
  return pooled
}

function compareNullableDesc(a: number | null, b: number | null): number {
  if (a === null && b === null) {
    return 0
  }
  if (a === null) {
    return 1
  }
  if (b === null) {
    return -1
  }
  return b - a
}

export function compareTop2ThenAverage(
  a: { top2Pct: number | null; average: number | null },
  b: { top2Pct: number | null; average: number | null },
): number {
  const byTop2 = compareNullableDesc(a.top2Pct, b.top2Pct)
  if (byTop2 !== 0) {
    return byTop2
  }
  return compareNullableDesc(a.average, b.average)
}

/**
 * The share at the scale's desirable place: the top-two box when higher is
 * better, the bottom-two box when reversed (a top-two statistic, 9-10 on
 * 1-10, never favorable share 8-10), and the middle band on a middle-is-best
 * scale, which has no desirable end.
 */
export function orientedTopTwoPct(
  row: { top2Pct: number; bottom2Pct: number; neutralPct?: number } | null | undefined,
  polarity: RatingPolarity,
): number | null {
  if (!row) {
    return null
  }
  if (polarity === 'middle_is_best') {
    return row.neutralPct ?? null
  }
  return polarity === 'higher_is_better' ? row.top2Pct : row.bottom2Pct
}

export function orientedTopTwoAriaPhrase(
  row: { top2Pct: number; bottom2Pct: number; neutralPct?: number } | null | undefined,
  polarity: RatingPolarity,
): string {
  const pct = orientedTopTwoPct(row, polarity) ?? 0
  if (polarity === 'middle_is_best') {
    return `${pct}% middle`
  }
  return polarity === 'higher_is_better' ? `${pct}% top-2` : `${pct}% bottom-2`
}

/**
 * The rating unit a compiled question declares for its (single) rating read:
 * `_canonical.unit` on a rating question, the matching `_canonical.measures`
 * entry on a matrix (by column name, else the only measure). Never inferred
 * from the scale's bounds.
 */
export function readDeclaredRatingUnit(question: unknown, columnName?: string): string | null {
  if (!question || typeof question !== 'object') {
    return null
  }
  const canonical = (question as Record<string, unknown>)._canonical
  if (!canonical || typeof canonical !== 'object') {
    return null
  }
  const record = canonical as Record<string, unknown>
  if (typeof record.unit === 'string' && record.unit) {
    return record.unit
  }
  const measures = Array.isArray(record.measures) ? (record.measures as Array<Record<string, unknown>>) : []
  const measure = columnName !== undefined
    ? measures.find((candidate) => candidate?.id === columnName)
    : measures.length === 1
      ? measures[0]
      : undefined
  return typeof measure?.unit === 'string' && measure.unit ? measure.unit : null
}

/** Policy bands for a declared unit, else the visual range split. */
export function threeBandsFor(unit: string | null | undefined, scaleMin: number, scaleMax: number): DivergingBands | null {
  return ratingPolicyBands(unit, scaleMin, scaleMax) ?? divergingBands(scaleMin, scaleMax)
}

/** Plain hyphen: "4-5" is copy the chart prints, and dashes are barred there. */
function rangeLabel(lo: number, hi: number): string {
  return lo === hi ? String(lo) : `${lo}-${hi}`
}

export function threeBandRangeLabels(bands: DivergingBands): { low: string; mid: string; high: string } {
  if (bands.hasNeutral) {
    return {
      low: rangeLabel(bands.scaleMin, bands.lowMax),
      mid: rangeLabel(bands.lowMax + 1, bands.highMin - 1),
      high: rangeLabel(bands.highMin, bands.scaleMax),
    }
  }
  return {
    low: rangeLabel(bands.scaleMin, bands.lowMax),
    mid: '',
    high: rangeLabel(bands.highMin, bands.scaleMax),
  }
}

export function paintThreeBandRow(
  row: DivergingRow,
  scaleMin: number,
  scaleMax: number,
  polarity: RatingPolarity,
  declaredBands?: DivergingBands | null,
): Array<ThreeBandPaint> {
  const bands = declaredBands ?? divergingBands(scaleMin, scaleMax)
  if (!bands) {
    return []
  }
  const labels = threeBandRangeLabels(bands)
  // A middle-is-best scale keeps its scale order: its favourable band is the
  // middle one, and both ends paint negative.
  const higherIsBetter = polarity !== 'lower_is_better'
  const middleIsBest = polarity === 'middle_is_best'
  const oriented = higherIsBetter
    ? { n: row.n, low: row.low, neutral: row.neutral, high: row.high }
    : { n: row.n, low: row.high, neutral: row.neutral, high: row.low }
  const layout = divergingLayout(oriented)
  const lowLeft = layout.spacerPct
  const neutralLeft = lowLeft + layout.lowPct
  const highLeft = neutralLeft + layout.neutralPct
  const leftBand: ThreeBandPaint = higherIsBetter
    ? {
        key: 'low',
        statistic: bands.statistic,
        leftPct: lowLeft,
        widthPct: layout.lowPct,
        color: THREE_BAND_NEGATIVE,
        percent: row.lowPct,
        label: labels.low,
        edge: 'left',
      }
    : {
        key: 'high',
        statistic: bands.statistic,
        leftPct: lowLeft,
        widthPct: layout.lowPct,
        color: THREE_BAND_NEGATIVE,
        percent: row.highPct,
        label: labels.high,
        edge: 'left',
      }
  const rightBand: ThreeBandPaint = higherIsBetter
    ? {
        key: 'high',
        statistic: bands.statistic,
        leftPct: highLeft,
        widthPct: layout.highPct,
        color: middleIsBest ? THREE_BAND_NEGATIVE : THREE_BAND_POSITIVE,
        percent: row.highPct,
        label: labels.high,
        edge: 'right',
      }
    : {
        key: 'low',
        statistic: bands.statistic,
        leftPct: highLeft,
        widthPct: layout.highPct,
        color: THREE_BAND_POSITIVE,
        percent: row.lowPct,
        label: labels.low,
        edge: 'right',
      }
  const paints: Array<ThreeBandPaint> = [leftBand]
  if (bands.hasNeutral) {
    paints.push({
      key: 'neutral',
      statistic: bands.statistic,
      leftPct: neutralLeft,
      widthPct: layout.neutralPct,
      color: middleIsBest ? THREE_BAND_POSITIVE : THREE_BAND_NEUTRAL,
      percent: row.neutralPct,
      label: labels.mid,
      edge: 'center',
    })
  }
  paints.push(rightBand)
  return paints
}
